import * as React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { FeatureLayoutConfiguration } from './FeatureLayoutConfigurationRepository';
import { FeatureLayoutCardDraggable } from './FeatureLayoutCardDraggable';

const useStyles = makeStyles(() =>
    createStyles({
        // Single flat sortable grid (see FeatureLayoutConfigurationList.tsx's own comment on
        // why) - itemsPerRow drives the column count via a hard percentage flex-basis per card
        // below, the same mechanism the old per-row layout used for its own columnShare.
        featureList: {
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
        },
        draggableWrapper: {
            boxSizing: 'border-box',
        },
    })
);

// Fixed share of the row based on the overall column count, not the number of items actually
// present - otherwise a partial last row (e.g. 3 items when itemsPerRow is 4) stretches its
// cards wider than full rows.
const draggableWrapperStyle = (itemsPerRow: number): React.CSSProperties => {
    const columnShare = `${100 / (itemsPerRow || 1)}%`;
    return { flex: `0 0 ${columnShare}`, maxWidth: columnShare };
};

interface FeatureLayoutConfigurationGridProps {
    featureLayoutConfigurationList: FeatureLayoutConfiguration[];
    onIsHiddenUpdated?: (featureGuid: string, isHidden: boolean) => void;
    itemsPerRow: number;
    isDragDisabled?: boolean;
    isInternalAdmin: boolean;
}

const FeatureLayoutConfigurationGridInner: React.FC<FeatureLayoutConfigurationGridProps> = props => {
    const classes = useStyles();

    // Stable across re-renders (unlike an inline arrow function) so FeatureLayoutCardDraggable's
    // own React.memo actually bails out instead of seeing a "new" callback prop every render.
    const onIsHiddenUpdated = React.useCallback((featureGuid: string, isHidden: boolean) => {
        if (props.onIsHiddenUpdated) {
            props.onIsHiddenUpdated(featureGuid, !isHidden);
        }
    // tslint:disable-next-line: align
    }, [props.onIsHiddenUpdated]);

    return (
        <div className={classes.featureList}>
            {props.featureLayoutConfigurationList.map((featureLayout, index) => (
                <div
                    className={classes.draggableWrapper}
                    key={featureLayout.featureGuid}
                    style={draggableWrapperStyle(props.itemsPerRow)}
                >
                    <FeatureLayoutCardDraggable
                        featureGuid={featureLayout.featureGuid}
                        feature={featureLayout.feature}
                        title={featureLayout.title}
                        description={featureLayout.description}
                        isHidden={featureLayout.isHidden}
                        onIsHiddenUpdated={onIsHiddenUpdated}
                        isDragDisabled={props.isDragDisabled}
                        infoUrl={featureLayout.infoUrl}
                        isInternalAdmin={props.isInternalAdmin}
                        animationDelay={index * 30}
                    />
                </div>
            ))}
        </div>
    );
};

// Same render gate as the old class's shouldComponentUpdate — if the list is the same
// array, the draggables are left alone (re-rendering them mid-drag breaks the drag library).
export const FeatureLayoutConfigurationGrid = React.memo(
    FeatureLayoutConfigurationGridInner,
    (previousProps, nextProps) =>
        previousProps.featureLayoutConfigurationList === nextProps.featureLayoutConfigurationList &&
        previousProps.itemsPerRow === nextProps.itemsPerRow
);
